import { ICheckClientPendingTask } from '~/types/server.types';
import { trackError } from '~/services/logger/error.utils';
import {
    ClientPendingTask,
    ClientPendingTaskType,
    IClientPendingTaskNode,
    PendingTaskStatus,
    TCPTActionReturnType
} from '~/common/hooks/ClientPendingTask/client-pending-task.types';
import type { ClientPendingTaskService } from '~/services/client-pending-task.service';
import { computed } from 'mobx';
import { WebSocketsService } from '~/controllers/web-sockets.service';
import { AuthenticatedGlobalContextValue } from '~/providers/base-global-context/authenticated-global.context';
import { IUserGetter } from '~/stores/base-user.store';

export const CPT_ROOM_NAME_SUFFIX = 'client_pending_tasks';

export class ClientPendingTaskController {
    private clientPendingTaskService: ClientPendingTaskService;
    private webSocketsService: WebSocketsService;
    private userGetter: Pick<IUserGetter, 'currUser'>;
    private readonly cptRoomName: string = CPT_ROOM_NAME_SUFFIX;
    private readonly _websocketPollingInterval: number = 6000;
    private readonly _pollingInterval: number = 2000;
    private readonly maxPollCount = 900;
    private runningTasksMap: Map<string, IClientPendingTaskNode> = new Map();
    private pollingTimeoutId: NodeJS.Timeout = null;
    private isWebSocketConnected: boolean = false;
    private unregisterFromWebSocket: () => void;

    constructor({
        clientPendingTaskService,
        userGetter,
        webSocketsService
    }: Pick<
        AuthenticatedGlobalContextValue,
        'clientPendingTaskService' | 'webSocketsService'
    > & {
        userGetter: Pick<IUserGetter, 'currUser'>;
    }) {
        this.clientPendingTaskService = clientPendingTaskService;
        this.userGetter = userGetter;
        this.webSocketsService = webSocketsService;
    }

    @computed
    get roomId() {
        return this.cptRoomName;
    }

    async onWebsocketConnected() {
        if (!this.isWebSocketConnected) {
            this.isWebSocketConnected = true;
            const namespace = {
                userId: this.userGetter.currUser._id
            };
            try {
                const clientPendingTaskToken =
                    await this.webSocketsService.getNamespaceToken(
                        namespace,
                        'client_pending_tasks'
                    );
                this.unregisterFromWebSocket = this.webSocketsService.joinRoom({
                    roomName: this.roomId,
                    callbackId: this.roomId,
                    callback: this.onWebSocketUpdate.bind(this),
                    namespace: namespace,
                    namespaceToken: clientPendingTaskToken
                });
                this.pendingTasksTimeoutHandler();
                this.cancelPolling();
            } catch {
                this.initiatePolling();
            }
        }
    }

    onWebsocketDisconnected() {
        this.isWebSocketConnected = false;
        this.unregisterFromWebSocket?.();
        this.initiatePolling();
    }

    private onWebSocketUpdate(data: any) {
        this.handlePendingTaskUpdate(data);
    }

    private get pollingInterval(): number {
        return this.isWebSocketConnected
            ? this._websocketPollingInterval
            : this._pollingInterval;
    }

    private get taskIds() {
        return Array.from(this.runningTasksMap.keys());
    }

    private get hasPendingTasks() {
        return this.runningTasksMap.size > 0;
    }

    private get isPollingOn() {
        return !!this.pollingTimeoutId;
    }

    private cleanupTasks() {
        this.runningTasksMap.forEach(taskNode => {
            this.cancelPendingTaskLocally(taskNode);
        });
        this.runningTasksMap.clear();
    }

    private cancelPolling() {
        if (this.pollingTimeoutId) {
            clearTimeout(this.pollingTimeoutId);
        }
        this.pollingTimeoutId = null;
    }

    private createTaskNode({
        taskId,
        taskType,
        onUpdateProgress
    }: {
        taskId?: string;
        taskType?: ClientPendingTaskType;
        onUpdateProgress?: (pendingTaskProgress: any) => void;
    }) {
        let reject;
        let resolve;
        const promise = new Promise((res, rej) => {
            resolve = res;
            reject = rej;
        });
        const taskNode: IClientPendingTaskNode = {
            canceled: false,
            pollCount: 0,
            promise,
            reject,
            resolve,
            onUpdateProgress,
            taskId,
            taskType
        };
        return taskNode;
    }

    private registerPendingTask(taskNode: IClientPendingTaskNode) {
        this.runningTasksMap.set(taskNode.taskId, taskNode);
        this.initiatePolling();
    }

    private initiatePolling() {
        if (!this.isPollingOn && this.hasPendingTasks) {
            // if websocket is connected we delay the first poll to give the websocket a chance to return
            const interval = this.isWebSocketConnected
                ? this.pollingInterval
                : 0;
            this.pollingTimeoutId = setTimeout(
                () => this.pendingTasksTimeoutHandler(),
                interval
            );
        }
    }

    private async pendingTasksTimeoutHandler() {
        if (this.hasPendingTasks) {
            const response =
                await this.clientPendingTaskService.checkClientPendingTasksByIds(
                    this.taskIds
                );
            this.taskIds.forEach(taskId => {
                const taskNode = this.runningTasksMap.get(taskId);
                const taskInResponse = response.find(
                    task => task._id === taskNode.taskId
                );
                if (taskNode.canceled || !taskInResponse) {
                    taskNode.reject('canceled');
                    this.removeTaskById(taskNode.taskId);
                    if (!taskInResponse) {
                        trackError({
                            error: new Error(
                                'found a client pending task which is not monitored on the server'
                            )
                        });
                    }
                    return;
                }
                this.handlePendingTaskUpdate(taskInResponse);
                if (taskNode.pollCount >= this.maxPollCount) {
                    taskNode.reject('Client pending task timed out');
                    this.cancelPendingTaskLocally(taskNode);
                }
                taskNode.pollCount++;
            });
        }

        if (this.hasPendingTasks) {
            this.pollingTimeoutId = setTimeout(
                () => this.pendingTasksTimeoutHandler(),
                this.pollingInterval
            );
        } else {
            this.pollingTimeoutId = null;
        }
    }

    private removeTaskById(taskId: string) {
        this.runningTasksMap.delete(taskId);
    }

    private handlePendingTaskUpdate(task: ICheckClientPendingTask) {
        const taskNode = this.runningTasksMap.get(task._id);
        if (!taskNode) {
            return;
        }

        const taskState = task.pending_task_state_cd;
        if (
            [PendingTaskStatus.Started, PendingTaskStatus.Pending].includes(
                taskState
            )
        ) {
            taskNode.onUpdateProgress?.(task.pending_task_progress);
            return;
        }
        if (taskState === PendingTaskStatus.Finished) {
            taskNode.resolve(task.pending_task_result);
            this.removeTaskById(taskNode.taskId);
            return;
        }
        taskNode.reject(task.pending_task_error_message);
        this.cancelPendingTaskLocally(taskNode);
    }

    private cancelPendingTaskLocally(task: IClientPendingTaskNode) {
        task.canceled = true;
        this.removeTaskById(task.taskId);
    }

    public async createClientPendingTask<T extends ClientPendingTaskType>({
        taskType,
        taskData,
        onUpdateProgress
    }: {
        taskType: T;
        taskData: ClientPendingTask[T]['taskData'];
        onUpdateProgress?: (pendingTaskProgress: any) => void;
    }): Promise<TCPTActionReturnType<T>> {
        const taskNode = this.createTaskNode({ taskType, onUpdateProgress });
        try {
            const response =
                await this.clientPendingTaskService.createClientPendingTask<T>(
                    taskType,
                    taskData
                );
            taskNode.taskId = response.task_id;
            this.registerPendingTask(taskNode);
        } catch (error) {
            trackError?.(error);
            throw error;
        }
        return { taskId: taskNode.taskId, promise: taskNode.promise };
    }

    public async performClientPendingTask<T extends ClientPendingTaskType>({
        taskType,
        taskData,
        onUpdateProgress
    }: {
        taskType: T;
        taskData: ClientPendingTask[T]['taskData'];
        onUpdateProgress?: ClientPendingTask[T]['onUpdateProgress'];
    }): Promise<ClientPendingTask[T]['returnType']> {
        const { promise } = await this.createClientPendingTask({
            taskType,
            taskData,
            onUpdateProgress
        });

        return promise;
    }

    public async cancelClientPendingTask(
        taskId: string,
        cancelMessage: string
    ) {
        try {
            await this.clientPendingTaskService.cancelClientPendingTask(
                taskId,
                cancelMessage
            );
            this.cancelTaskPolling(taskId);
        } catch (error) {
            trackError?.(error);
        }
    }

    public async subscribeToTaskByType<T extends ClientPendingTaskType>({
        taskType,
        onUpdateProgress
    }: {
        taskType: T;
        onUpdateProgress?: (pendingTaskProgress: any) => void;
    }): Promise<TCPTActionReturnType<T>> {
        const existingTask = Array.from(this.runningTasksMap.values()).find(
            task => task.taskType === taskType
        );
        const taskNode =
            existingTask ||
            this.createTaskNode({
                taskType,
                onUpdateProgress
            });

        try {
            const response =
                await this.clientPendingTaskService.checkClientPendingTaskByType(
                    taskType
                );
            if (!response[0]?._id) {
                taskNode.reject('task not found');
                return { taskId: undefined, promise: taskNode.promise };
            }
            taskNode.taskId = response[0]._id;
            this.registerPendingTask(taskNode);
        } catch (error) {
            trackError(error);
        }
        return { taskId: taskNode.taskId, promise: taskNode.promise };
    }

    public subscribeToTaskById<T extends ClientPendingTaskType>({
        taskId,
        taskType,
        onUpdateProgress
    }: {
        taskId: string;
        taskType: T;
        onUpdateProgress?: (pendingTaskProgress: any) => void;
    }): TCPTActionReturnType<T> {
        const taskNode = this.createTaskNode({
            taskId,
            taskType,
            onUpdateProgress
        });
        this.registerPendingTask(taskNode);
        return { taskId: taskNode.taskId, promise: taskNode.promise };
    }

    public cancelTaskPolling(taskId: string) {
        const taskNodeToCancel = this.runningTasksMap.get(taskId);
        if (taskNodeToCancel) {
            this.cancelPendingTaskLocally(taskNodeToCancel);
        } else {
            trackError({
                error: new Error('could not find the task promise to cancel')
            });
        }
    }

    public onLogout() {
        this.cleanupTasks();
        this.cancelPolling();
    }
}
